import { defineStore } from "pinia";
import { ref } from "vue";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { useCurrentUserStore } from "@stores/currentUser";
import { useSurveyStore } from "@stores/survey/survey";
import { useSurveyCardsStore } from "@stores/survey/surveyCards";
import { useAssistantStore } from "@/stores/assistant";
import { captureEvent } from "@/lib/analytics";

interface ReviewUser {
  id: number;
  full_name: string;
  initials: string;
}

interface ReviewComment {
  id: number;
  created_at: string;
  edited_at: string | null;
  deleted: boolean;
  body: string | null;
  mention_ids: number[];
  posted_via: string | null;
  user: ReviewUser;
}

type ThreadStatus = "open" | "resolved";
type ThreadResolution = "applied" | "dismissed" | "resolved" | "fixed";
interface PanelState {
  status: ThreadStatus;
  openCard: string | null;
}

const NEW_TOKEN_PREFIX = "new:";
type SuggestionFields = Record<string, unknown> & { name?: string };
interface Suggestion {
  summary: string[];
  changes: Record<string, SuggestionFields | null>;
}

interface ReviewThread {
  id: number;
  status: ThreadStatus;
  suggestion: Suggestion | null;
  lint_code: string | null;
  stale_cards: string[];
  resolution: ThreadResolution | null;
  resolved_at: string | null;
  created_at: string;
  card_token: string | null;
  created_by: ReviewUser;
  resolved_by: ReviewUser | null;
  review_comments: ReviewComment[];
}

interface MentionableUser extends ReviewUser {
  kind: "client" | "csm";
}

type ReviewChannelMessage = { action: "thread_updated"; thread: ReviewThread };

export const SURVEY_GROUP = "survey";

export const useReviewCommentsStore = defineStore("reviewComments", () => {
  const threads = ref<ReviewThread[]>([]);
  const mentionableUsers = ref<MentionableUser[]>([]);

  const panel = ref<PanelState>({ status: "open", openCard: SURVEY_GROUP });

  const currentUserStore = useCurrentUserStore();
  const surveyStore = useSurveyStore();

  const basePath = () => `surveys/${surveyStore.token}/review_threads`;
  const isMine = (comment: ReviewComment) => comment.user.id === currentUserStore.getUser?.id;

  const request = async (run: () => Promise<void>) => {
    try {
      await run();
    } catch (err) {
      handleError(err);
    }
  };

  const findThread = (threadId: number) => threads.value.find((thread) => thread.id === threadId);

  const prune = (thread: ReviewThread) => {
    const comments = thread.review_comments;
    while (comments.length && comments[comments.length - 1].deleted) comments.pop();
    if (!comments.some((comment) => !comment.deleted)) {
      threads.value = threads.value.filter((other) => other.id !== thread.id);
    }
  };

  const upsertThread = (incoming: ReviewThread) => {
    const existing = findThread(incoming.id);
    if (existing) Object.assign(existing, incoming);
    else threads.value.push(incoming);
    const thread = findThread(incoming.id);
    if (thread) prune(thread);
  };

  const upsertComment = (threadId: number, incoming: ReviewComment) => {
    const thread = findThread(threadId);
    if (!thread) return;
    const existing = thread.review_comments.find((comment) => comment.id === incoming.id);
    if (existing) Object.assign(existing, incoming);
    else thread.review_comments.push(incoming);
  };

  const applyCommentDeletion = (thread: ReviewThread, commentId: number) => {
    const index = thread.review_comments.findIndex((comment) => comment.id === commentId);
    if (index === -1) return;
    const comment = thread.review_comments[index];
    if (index < thread.review_comments.length - 1) {
      comment.deleted = true;
      comment.body = null;
      comment.mention_ids = [];
    } else {
      thread.review_comments.splice(index, 1);
    }
    prune(thread);
  };

  const markReopened = (thread: ReviewThread) => {
    thread.status = "open";
    thread.resolved_by = null;
    thread.resolved_at = null;
    if (thread.resolution !== "applied") thread.resolution = null;
  };

  const loadThreads = async (fetching: ReturnType<typeof useMyFetch>) => {
    const { response, data } = await fetching.json<{ review_threads: ReviewThread[] }>();
    if (!response.value?.ok) return;
    threads.value = data.value?.review_threads ?? [];
    threads.value.slice().forEach(prune);
  };

  const lint = () => request(() => loadThreads(useMyFetch(`${basePath()}/lint`).post()));

  const fetchThreads = () =>
    request(async () => {
      await loadThreads(useMyFetch(basePath()));
      const { response: usersResponse, data: usersData } = await useMyFetch(
        `surveys/${surveyStore.token}/review_mentionable_users`,
      ).json<{ users: MentionableUser[] }>();
      if (usersResponse.value?.ok) mentionableUsers.value = usersData.value?.users ?? [];
    });

  const concernsCard = (thread: ReviewThread, cardToken: string) =>
    thread.card_token === cardToken || cardToken in (thread.suggestion?.changes ?? {});
  const threadsForCard = (cardToken: string) => threads.value.filter((thread) => concernsCard(thread, cardToken));
  const openCount = (cardToken: string) =>
    threadsForCard(cardToken).filter((thread) => thread.status === "open").length;
  const applicable = (thread: ReviewThread) => thread.status === "open" && !!thread.suggestion;
  const cardLabel = (thread: ReviewThread, token: string) =>
    token.startsWith(NEW_TOKEN_PREFIX)
      ? thread.suggestion?.changes[token]?.name ?? "New card"
      : useSurveyCardsStore().findCardByToken({ token })?.name ?? token;
  const alsoTouchedCards = (thread: ReviewThread) =>
    Object.keys(thread.suggestion?.changes ?? {})
      .filter((token) => token !== thread.card_token)
      .map((token) => cardLabel(thread, token));

  const composerFocusAt = ref(0);
  const openCardComments = (cardToken: string) => {
    panel.value.openCard = cardToken;
    composerFocusAt.value = Date.now();
  };
  const toggleCard = (key: string) => {
    panel.value.openCard = panel.value.openCard === key ? null : key;
  };
  const matchesPanel = (thread: ReviewThread) => thread.status === panel.value.status;

  const createThread = (cardToken: string | null, body: string, mentions: number[] = []) =>
    request(async () => {
      const { data, error } = await useMyFetch(basePath())
        .post({ review_thread: { body, card_token: cardToken, mention_ids: mentions } })
        .json<ReviewThread>();
      if (error.value) throw error.value;
      if (data.value) upsertThread(data.value);

      captureEvent("review_thread_created", {
        on_card: cardToken !== null,
        mentions_count: mentions.length,
      });
    });

  const addComment = (thread: ReviewThread, body: string, mentions: number[] = []) =>
    request(async () => {
      const { data, error } = await useMyFetch(`${basePath()}/${thread.id}/review_comments`)
        .post({ review_comment: { body, mention_ids: mentions } })
        .json<ReviewComment>();
      if (error.value) throw error.value;
      if (!data.value) return;
      upsertComment(thread.id, data.value);
      markReopened(thread);

      captureEvent("review_comment_added", {
        thread_id: thread.id,
        mentions_count: mentions.length,
      });
    });

  const updateComment = (thread: ReviewThread, comment: ReviewComment, body: string, mentions: number[] = []) =>
    request(async () => {
      const { data, error } = await useMyFetch(`${basePath()}/${thread.id}/review_comments/${comment.id}`)
        .patch({ review_comment: { body, mention_ids: mentions } })
        .json<ReviewComment>();
      if (error.value) throw error.value;
      if (data.value) upsertComment(thread.id, data.value);

      captureEvent("review_comment_updated", { thread_id: thread.id });
    });

  const deleteComment = (thread: ReviewThread, comment: ReviewComment) =>
    request(async () => {
      const { response, error } = await useMyFetch(`${basePath()}/${thread.id}/review_comments/${comment.id}`).delete();
      if (error.value) throw error.value;
      if (response.value?.ok) {
        applyCommentDeletion(thread, comment.id);
        captureEvent("review_comment_deleted", { thread_id: thread.id });
      }
    });

  const patchStatus = async (thread: ReviewThread, status: "open" | "resolved") => {
    const { data, error } = await useMyFetch(`${basePath()}/${thread.id}`)
      .patch({ review_thread: { status } })
      .json<ReviewThread>();
    if (error.value) throw error.value;
    if (data.value) upsertThread(data.value);
  };

  const resolveThread = (thread: ReviewThread) =>
    request(async () => {
      await patchStatus(thread, "resolved");
      captureEvent("review_thread_resolved", { thread_id: thread.id });
    });

  const reopenThread = (thread: ReviewThread) =>
    request(async () => {
      await patchStatus(thread, "open");
      captureEvent("review_thread_reopened", { thread_id: thread.id });
    });

  const applyingId = ref<number | null>(null);
  const applySuggestion = async (thread: ReviewThread) => {
    if (applyingId.value) return false;
    applyingId.value = thread.id;
    try {
      const { data, error } = await useMyFetch(`${basePath()}/${thread.id}/apply_suggestion`)
        .post()
        .json<ReviewThread>();
      if (error.value) throw error.value;
      if (data.value) upsertThread(data.value);
      await useSurveyCardsStore().refreshCards();

      captureEvent("review_suggestion_applied", {
        thread_id: thread.id,
        changes_count: Object.keys(thread.suggestion?.changes ?? {}).length,
      });
      return true;
    } catch (err) {
      handleError(err);
      return false;
    } finally {
      applyingId.value = null;
    }
  };

  const requestAiReview = () => {
    const assistant = useAssistantStore();
    const prompt = assistant.suggestionGroups.Review?.[0];
    if (prompt && assistant.runInBackground(prompt)) captureEvent("review_ai_requested");
  };

  const handleChannelMessage = (message: ReviewChannelMessage) => {
    if (message.action === "thread_updated") upsertThread(message.thread);
  };

  const reset = () => {
    threads.value = [];
    mentionableUsers.value = [];
    panel.value = { status: "open", openCard: SURVEY_GROUP };
  };

  return {
    threads,
    panel,
    mentionableUsers,
    composerFocusAt,
    isMine,
    fetchThreads,
    lint,
    threadsForCard,
    openCount,
    openCardComments,
    toggleCard,
    matchesPanel,
    addComment,
    updateComment,
    deleteComment,
    createThread,
    resolveThread,
    reopenThread,
    applySuggestion,
    applyingId,
    applicable,
    alsoTouchedCards,
    requestAiReview,
    handleChannelMessage,
    reset,
  };
});
